import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Grid,
  Typography,
  Paper,
  Divider,
  RadioGroup,
  Radio,
  Button,
  FormControlLabel,
  Alert,
  Link,
} from '@mui/material';

export const boolToRadioValue = (value: boolean | null) => {
  if (value === null) return ''
  return value ? 'sim' : 'nao'
}

export const validateStep1 = (payload: {
  hasSim: boolean | null
  simActive: boolean | null
  simEstablishments: number | null
  hasVet: boolean | null
  joinedSusaf: boolean | null
  wantsSusaf: boolean | null
}) => {
  const newErrors: Record<string, string> = {}
  if (payload.hasSim === null) newErrors.hasSim = 'Campo obrigatório.'

  if (payload.hasSim) {
    if (payload.simActive === null) newErrors.simActive = 'Campo obrigatório.'
    if (
      payload.simEstablishments === null ||
      Number.isNaN(Number(payload.simEstablishments)) ||
      !Number.isInteger(Number(payload.simEstablishments)) ||
      Number(payload.simEstablishments) < 0
    )
      newErrors.simEstablishments = 'Informe um número inteiro válido.'
    if (payload.hasVet === null) newErrors.hasVet = 'Campo obrigatório.'
    if (payload.joinedSusaf === null) newErrors.joinedSusaf = 'Campo obrigatório.'
    if (payload.wantsSusaf === null) newErrors.wantsSusaf = 'Campo obrigatório.'
  }

  return newErrors
}

export interface Step1SimData {
  referenceYear: number;
  hasSim: boolean | null;
  simActive: boolean | null;
  simEstablishments: number | null;
  hasVet: boolean | null;
  joinedSusaf: boolean | null;
  wantsSusaf: boolean | null;
}

interface Step1Props {
  onNext: () => void;
  onChange: (data: Step1SimData) => void;
  data: Step1SimData;
  disabled?: boolean;
}

const Step1: React.FC<Step1Props> = ({
  onNext,
  onChange,
  data,
  disabled = false,
}) => {
  const currentYear = new Date().getFullYear();

  const [hasSim, setHasSim] = useState<boolean | null>(data.hasSim ?? null);
  const [simActive, setSimActive] = useState<boolean | null>(data.simActive ?? null);
  const [simEstablishments, setSimEstablishments] = useState<number | null>(data.simEstablishments ?? null);
  const [hasVet, setHasVet] = useState<boolean | null>(data.hasVet ?? null);
  const [joinedSusaf, setJoinedSusaf] = useState<boolean | null>(data.joinedSusaf ?? null);
  const [wantsSusaf, setWantsSusaf] = useState<boolean | null>(data.wantsSusaf ?? null);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    setHasSim(data.hasSim ?? null);
    setSimActive(data.simActive ?? null);
    setSimEstablishments(data.simEstablishments ?? null);
    setHasVet(data.hasVet ?? null);
    setJoinedSusaf(data.joinedSusaf ?? null);
    setWantsSusaf(data.wantsSusaf ?? null);
  }, [data]);

  useEffect(() => {
    onChange({
      referenceYear: data.referenceYear || currentYear - 1,
      hasSim,
      simActive,
      simEstablishments,
      hasVet,
      joinedSusaf,
      wantsSusaf,
    });
    // eslint-disable-next-line
  }, [hasSim, simActive, simEstablishments, hasVet, joinedSusaf, wantsSusaf]);

  // Preencher automaticamente o ano de referência com ano anterior ao atual se não estiver preenchido
  useEffect(() => {
    if (!data.referenceYear || data.referenceYear === 0) {
      onChange({
        ...data,
        referenceYear: currentYear - 1,
      });
    }
  }, []);

  const handleSubmit = () => {
    const newErrors = validateStep1({
      hasSim,
      simActive,
      simEstablishments,
      hasVet,
      joinedSusaf,
      wantsSusaf,
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) onNext();
  };

  // Função para converter boolean para string de valor do radio


  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Serviço de Inspeção Municipal (SIM)
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <TextField
          label="Ano de referência do Questionário"
          type="number"
          value={data.referenceYear}
          onChange={e => onChange({
            ...data,
            referenceYear: Number(e.target.value)
          })}
          inputProps={{ min: 1900, max: 2100 }}
          fullWidth
          required
          disabled={true}
          sx={{ opacity: 0.5 }}
          margin="normal"
        />

        <Alert severity="info" sx={{ mb: 3 }}>
          Preencha todos os campos obrigatórios. Campos condicionais serão exibidos conforme suas respostas.
        </Alert>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              O município possui Serviço de Inspeção Municipal (SIM) implantado? <span style={{ color: 'red' }}>*</span>
            </Typography>
            <RadioGroup
              row
              value={boolToRadioValue(hasSim)}
              onChange={e => {
                const value = e.target.value === 'sim';
                setHasSim(value);
                // Reset campos dependentes quando mudar hasSim
                if (!value) {
                  setSimActive(null);
                  setSimEstablishments(null);
                  setHasVet(null);
                  setJoinedSusaf(null);
                  setWantsSusaf(null);
                }
              }}
              name="has-sim"
            >
              <FormControlLabel value="sim" control={<Radio />} label="Sim" />
              <FormControlLabel value="nao" control={<Radio />} label="Não" />
            </RadioGroup>
            {errors.hasSim && <Typography color="error">{errors.hasSim}</Typography>}
          </Grid>

          {hasSim && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  O Serviço de Inspeção Municipal (SIM) está ativo? <span style={{ color: 'red' }}>*</span>
                </Typography>
                <RadioGroup
                  row
                  value={boolToRadioValue(simActive)}
                  onChange={e => {
                    const value = e.target.value === 'sim';
                    setSimActive(value);
                  }}
                  name="sim-active"
                >
                  <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                  <FormControlLabel value="nao" control={<Radio />} label="Não" />
                </RadioGroup>

                {errors.simActive && <Typography color="error">{errors.simActive}</Typography>}
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Quantos empreendimentos/estabelecimentos possuem SIM no município?"
                  type="number"
                  value={String(simEstablishments).length > 0 ? String(simEstablishments) : 0}
                  onChange={e => setSimEstablishments(Number(e.target.value))}
                  error={!!errors.simEstablishments}
                  helperText={errors.simEstablishments}
                  margin="dense"
                  required
                  fullWidth
                  inputProps={{ min: 0, step: 1 }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  O município possui médico veterinário exclusivo disponível para atender o SIM? <span style={{ color: 'red' }}>*</span>
                </Typography>
                <RadioGroup
                  row
                  value={boolToRadioValue(hasVet)}
                  onChange={e => setHasVet(e.target.value === 'sim')}
                  name="has-vet"
                >
                  <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                  <FormControlLabel value="nao" control={<Radio />} label="Não" />
                </RadioGroup>
                {errors.hasVet && <Typography color="error">{errors.hasVet}</Typography>}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  O município fez adesão ao Sistema Unificado Estadual de Sanidade Agroindustrial Familiar e de Pequeno Porte - SUSAF/MT? <span style={{ color: 'red' }}>*</span>
                </Typography>
                <RadioGroup
                  row
                  value={boolToRadioValue(joinedSusaf)}
                  onChange={e => setJoinedSusaf(e.target.value === 'sim')}
                  name="joined-susaf"
                >
                  <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                  <FormControlLabel value="nao" control={<Radio />} label="Não" />
                </RadioGroup>
                {errors.joinedSusaf && <Typography color="error">{errors.joinedSusaf}</Typography>}

              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  O município tem interesse em aderir ao SUSAF/MT? <span style={{ color: 'red' }}>*</span>
                </Typography>
                <RadioGroup
                  row
                  value={boolToRadioValue(wantsSusaf)}
                  onChange={e => setWantsSusaf(e.target.value === 'sim')}
                  name="wants-susaf"
                >
                  <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                  <FormControlLabel value="nao" control={<Radio />} label="Não" />
                </RadioGroup>
                {errors.wantsSusaf && <Typography color="error">{errors.wantsSusaf}</Typography>}
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          size="large"
          disabled={disabled}
        >
          Salvar e continuar
        </Button>
      </Box>
    </Box>
  );
};

export default Step1;
